import React, { useState } from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { ChevronDown, ChevronRight, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

const severityConfig = {
    ERROR: { icon: AlertCircle, color: "text-red-600", badge: "bg-red-100 text-red-700" },
    WARNING: { icon: AlertTriangle, color: "text-yellow-600", badge: "bg-yellow-100 text-yellow-700" },
    INFO: { icon: Info, color: "text-blue-600", badge: "bg-blue-100 text-blue-700" },
};

function CodeScanFindingCard({ finding, onUpdateStatus }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const config = severityConfig[finding.severity] || severityConfig.INFO;
    const Icon = config.icon;

    return (
        <div className={`border rounded-lg ${finding.status !== "OPEN" ? "opacity-60" : ""}`}>
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50"
            >
                {expanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                )}
                <Icon className={`w-4 h-4 ${config.color} shrink-0`} />
                <span className="text-sm font-medium text-gray-900 truncate flex-1">
                    {finding.ruleId}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                    {finding.severity}
                </span>
                {finding.filePath && (
                    <span className="text-xs text-gray-500 truncate max-w-[150px]">
                        {finding.filePath}:{finding.lineStart}
                    </span>
                )}
            </button>
            {expanded && (
                <div className="px-3 pb-3 border-t bg-gray-50 space-y-2">
                    <p className="text-sm text-gray-700 mt-2">{finding.message}</p>
                    {finding.codeSnippet && (
                        <pre className="text-xs bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto">
                            {finding.codeSnippet}
                        </pre>
                    )}
                    {finding.remediation && (
                        <div className="text-sm">
                            <span className="font-medium text-gray-700">
                                {t("appPreview.remediation") || "Remediation"}:
                            </span>{" "}
                            <span className="text-gray-600">{finding.remediation}</span>
                        </div>
                    )}
                    {finding.status === "OPEN" && (
                        <div className="flex gap-2 pt-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onUpdateStatus(finding.id, "DISMISSED")}
                            >
                                {t("appPreview.dismiss") || "Dismiss"}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onUpdateStatus(finding.id, "ACKNOWLEDGED")}
                            >
                                {t("appPreview.acknowledge") || "Acknowledge"}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default React.memo(CodeScanFindingCard);
